import React from "react";
import { Alert } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { OrderStatus } from "client/api-v2";
import type { OrderDto } from "client/api-v2";
import { canReopenOrder, ReopenOrderAction } from "areas/orders/components/OrderLifecycleActions";

const lockedByStatus: Record<OrderStatus, boolean> = {
  [OrderStatus.New]: false,
  [OrderStatus.Quote]: false,
  [OrderStatus.Accepted]: true,
  [OrderStatus.Cancelled]: true,
  [OrderStatus.Expired]: true,
};

export function isOrderLocked(status: OrderStatus): boolean {
  return lockedByStatus[status];
}

export function isOrderClosedToEdits(order: Pick<OrderDto, "status" | "isArchived">): boolean {
  return order.isArchived || isOrderLocked(order.status);
}

function lockedMessage(order: OrderDto): string {
  if (order.isArchived) {
    const when = order.archivedAt === null ? "" : ` on ${formatDateOnly(getOrParseDateTime(order.archivedAt))}`;
    return `This order was archived${when} and can no longer be changed. Its status is still ${order.status}.`;
  }

  if (order.status === OrderStatus.Accepted) {
    return "This order is Accepted and is now an immutable commercial record.";
  }

  const state = order.status === OrderStatus.Expired ? "This quote has expired." : `This order is ${order.status}.`;
  if (order.canReopen) {
    return `${state} It can be reopened, as there’s no other quote for this account in an actionable status (New or Quote).`;
  }

  if (order.reopenBlockedByBillingAccountChange) {
    return `${state} It can’t be reopened because its subscription is no longer billed to the billing account it was quoted for. Create a new quote instead.`;
  }
  if (order.blockingQuoteOdNumber) {
    return `${state} It can’t be reopened while quote ${order.blockingQuoteOdNumber} for this account is in an actionable status (New or Quote).`;
  }
  return state;
}

export function LockedOrderBanner({
  order,
  onOrderChanged,
  sx,
}: {
  order: OrderDto;
  onOrderChanged: (order: OrderDto) => void;
  sx?: SxProps<Theme>;
}) {
  return (
    <Alert
      severity="info"
      sx={sx}
      action={canReopenOrder(order) ? <ReopenOrderAction order={order} onChanged={onOrderChanged} /> : undefined}
    >
      {lockedMessage(order)}
    </Alert>
  );
}
